import React from "react";
import { Link } from "react-router-dom";
import { AlertOctagon } from "lucide-react";
import { isLegalDraft } from "../../lib/legalConfig";
import { BrandWatermark, BrandMonogram } from "../Brand";
import LegalFooter from "./LegalFooter";

/**
 * Shared shell for all /legal/* pages. Renders:
 *  - A loud "Draft" banner until LEGAL.REVIEWED flips to true.
 *  - A breadcrumb back to /
 *  - The page title + effective-date stamp
 *  - The actual page content (children) inside a readable prose container
 *  - The legitimacy footer
 */
export default function LegalShell({ title, effectiveDate, lastUpdated, intro, children, testid }) {
  return (
    <div className="xch-app-bg min-h-screen" data-testid={testid || "legal-shell"}>
      {isLegalDraft() && (
        <div
          className="bg-rose-700 text-white text-center text-xs sm:text-sm py-2 px-4 font-medium"
          data-testid="legal-draft-banner"
        >
          <AlertOctagon className="inline w-4 h-4 mr-1 -mt-0.5" />
          <strong>Draft — pending legal review.</strong>{" "}
          The text on this page is placeholder scaffolding and is{" "}
          <span className="underline">not legally binding</span>. Final terms
          will be published once reviewed by counsel.
        </div>
      )}

      <header className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="text-muted-foreground hover:text-foreground">Home</Link>
          <span className="text-muted-foreground">/</span>
          <Link to="/legal" className="text-muted-foreground hover:text-foreground">Legal</Link>
          <span className="text-muted-foreground">/</span>
          <span className="font-semibold text-foreground" data-testid="legal-title-crumb">{title}</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10 relative">
        {/* Decorative branded watermark */}
        <BrandWatermark size={320} opacity={0.045} position="top-right" />

        <div className="flex items-start gap-3 mb-2">
          <BrandMonogram size={36} className="shrink-0 mt-1" />
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-foreground" data-testid="legal-page-title">
            {title}
          </h1>
        </div>
        <div className="text-xs text-muted-foreground mb-8 flex flex-wrap gap-x-4 gap-y-1" data-testid="legal-metadata">
          {effectiveDate && <span>Effective: <strong className="text-foreground">{effectiveDate}</strong></span>}
          {lastUpdated && <span>Last updated: <strong className="text-foreground">{lastUpdated}</strong></span>}
        </div>

        {intro && (
          <div className="text-base leading-relaxed text-foreground mb-8 bg-muted/30 rounded-xl p-4 border border-border">
            {intro}
          </div>
        )}

        <article className="legal-prose space-y-6 text-sm sm:text-base leading-relaxed text-foreground">
          {children}
        </article>
      </main>

      <LegalFooter />
    </div>
  );
}

// Helper for consistent section heading styling
export const LegalSection = ({ id, n, title, children }) => (
  <section id={id} data-testid={`legal-section-${id}`}>
    <h2 className="font-display text-xl sm:text-2xl font-extrabold text-foreground mt-8 mb-3">
      {n ? <span className="text-primary mr-2 tabular-nums">{n}.</span> : null}
      {title}
    </h2>
    <div className="space-y-3 text-foreground/90">{children}</div>
  </section>
);
